import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Badge } from 'components/Badge/Badge'
import { Button, Flex, colors } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { Cell } from 'components/HonestTable/HonestTable.styled'
import { IKitchenSuggestionsTabProps } from 'types/kitchen/kitchenTabProps.interface'
import { IKitchenDish } from 'types/kitchen/kitchenDish.interface'
import { kitchenSuggestionReasons } from '../kitchenData'
import { KitchenTag } from '../Kitchen.styled'
import { toast } from 'react-toastify'

interface IRankedDish {
  id: number
  dish: IKitchenDish
  cookableCount: number
  expiry: number
  cost: number
  rank: number
}

export const SuggestionsTab = ({ dishes, cookable, expiryScore, costPerPortion }: IKitchenSuggestionsTabProps) => {
  const ranked: IRankedDish[] = dishes
    .filter((d) => d.norm.length > 0)
    .map((d) => ({
      id: d.id,
      dish: d,
      cookableCount: cookable(d) || 0,
      expiry: expiryScore(d),
      cost: costPerPortion(d),
      rank: 0,
    }))
    .sort((a, b) => b.expiry - a.expiry || b.cookableCount - a.cookableCount)
    .map((r, i) => ({ ...r, rank: i + 1 }))

  const columns: IHonestTableColumn<IRankedDish>[] = [
    {
      id: 'rank',
      header: 'Rank',
      cell: (r) => (
        <BodyText weight='700' style={{ color: r.rank === 1 ? colors.orange : undefined }}>
          #{r.rank}
        </BodyText>
      ),
    },
    {
      id: 'dish',
      header: 'Dish',
      sticky: true,
      minWidth: '12rem',
      cell: (r) => (
        <Cell>
          <BodyText weight='600'>{r.dish.name}</BodyText>
          <Flex wrap gap='0.25rem' mt='4'>
            {r.dish.tags.map((t) => (
              <KitchenTag key={t}>{t}</KitchenTag>
            ))}
          </Flex>
        </Cell>
      ),
    },
    {
      id: 'cookable',
      header: 'Cookable portions',
      cell: (r) => (
        <BodyText weight='700' style={{ fontSize: '1rem' }}>
          {r.cookableCount}
        </BodyText>
      ),
    },
    {
      id: 'why',
      header: 'Why',
      cell: (r) => (
        <OtherText style={{ color: r.expiry === 2 ? colors.red : r.expiry === 1 ? colors.orange : undefined }}>
          {kitchenSuggestionReasons[r.dish.id] || '—'}
        </OtherText>
      ),
    },
    {
      id: 'cost',
      header: 'Cost / portion',
      cell: (r) => <BodyText>${r.cost.toFixed(2)}</BodyText>,
    },
    {
      id: 'demand',
      header: 'Demand fit (Dinner T+0)',
      cell: (r) => {
        if (r.dish.id === 3) {
          return (
            <Badge text="64 vs 84 Regular — short 20" red />
          )
        }
        if (r.dish.tags.includes('Vegetarian')) {
          return (
            <Badge text="Covers Veg 24" green />
          )
        }
        return (
          <Badge text="Covers Regular" green />
        )
      },
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (r) => (
        <Flex gap='0.375rem' justend width='100%'>
          <Button
            orangeBorder
            onClick={() => toast.success(`${r.dish.name} assigned to Dinner draft (Menu Planner)`)}
          >
            To menu
          </Button>
          {r.dish.id === 3 && (
            <Button orangeBorder onClick={() => toast.success('Draft PR created: Tilapia +2.8 kg (covers Dinner shortfall, SKU-resolved)')}>
              Draft PR
            </Button>
          )}
        </Flex>
      ),
    },
  ]

  return (
    <Container>
      <ContainerHeader>
        <TitleText weight='700'>What to cook first — Main Kitchen (deterministic: ledger query, FEFO pressure ranking)</TitleText>
      </ContainerHeader>
      <ContainerContent gap='0.625rem'>
        <HonestTable columns={columns} data={ranked} keyField='id' />
        <OtherText grey>
          V2 (agentic): a Menu Optimization Agent will propose multi-day menus balancing demand, expiry, cost and variety — as advisory cases through the AIM workflow layer.
        </OtherText>
      </ContainerContent>
    </Container>
  )
}
